<template>
  <div :data-view-id="componentUniqueTitle || uniqueTitle">
    <b-container fluid class="calendar-container">
      <ViewTitle :title="title" />
      <b-row v-if="visiblePresetFilterButtons.length" class="mb-2">
        <b-col>
          <PresetFilterButtons
            :buttons="visiblePresetFilterButtons"
            :active-ids="activePresetFilterIds"
            @toggle="onPresetFilterToggle"
          />
        </b-col>
      </b-row>
      <b-row v-if="searchEnabled" class="mb-2">
        <b-col>
          <ViewSearch :model-value="searchText" @update:model-value="onSearchInput" />
        </b-col>
      </b-row>
      <b-row v-if="userFiltering" class="mb-3">
        <b-col>
          <RuntimeFilterBuilder
            ref="filterBuilder"
            :field-groups="filterFieldGroups"
            :initial-filters="resolvedInitialFilters"
            :initial-match-mode="resolvedInitialMatchMode"
            @filter-changed="onFilterChanged"
          />
        </b-col>
      </b-row>
      <div class="calendar-toolbar">
        <div class="calendar-layouts" role="group" aria-label="Calendar layout">
          <button
            v-for="choice in layoutChoices"
            :key="choice.id"
            type="button"
            class="calendar-nav"
            :class="{ 'is-active': layout === choice.id }"
            :aria-pressed="layout === choice.id ? 'true' : 'false'"
            @click="setLayout(choice.id)"
          >{{ choice.label }}</button>
        </div>
        <div class="calendar-nav-group">
          <button type="button" class="calendar-nav" @click="shiftPeriod(-1)">Previous</button>
          <button type="button" class="calendar-nav" @click="goToday">Today</button>
          <div class="calendar-month">{{ periodLabel }}</div>
          <button type="button" class="calendar-nav" @click="shiftPeriod(1)">Next</button>
        </div>
        <div class="calendar-actions">
          <button v-if="addFormUniqueTitle" type="button" class="calendar-nav" :disabled="!selectedDay" :title="selectedDay ? 'Add a record on the selected day' : 'Choose a day first'" @click="addOnSelectedDay">Add record</button>
          <button type="button" class="calendar-nav" :disabled="icalDownloading" @click="downloadIcal">
            {{ icalDownloading ? 'Downloading…' : 'Download iCal' }}
          </button>
        </div>
      </div>
      <div v-if="layout === 'month' || layout === 'week'" class="calendar-grid" :class="{ 'is-week': layout === 'week' }" role="group" :aria-label="periodLabel" :data-layout="layout">
        <div v-for="name in weekdayNames" :key="name" class="calendar-dow">{{ name }}</div>
        <template v-for="(week, weekIndex) in weeks" :key="weekIndex">
          <div
            v-for="day in week"
            :key="day.key"
            class="calendar-day"
            :class="{ 'is-outside': !day.inMonth, 'is-today': day.isToday, 'is-selected': day.key === selectedDay }"
            @keydown="onDayKey(day, $event)"
          >
            <button type="button" class="calendar-day-number" :data-day="day.key" :aria-current="day.key === selectedDay ? 'date' : undefined" :aria-label="day.date.toLocaleDateString()" aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown" @click="selectDay(day)">{{ day.date.getDate() }}</button>
            <button
              v-for="event in visibleEvents(day)"
              :key="eventKey(event)"
              type="button"
              class="calendar-event"
              :style="eventTitleStyle(event).style"
              @click="openEvent(day, event)"
            >
              <span v-if="!eventTitleStyle(event).hidden" class="calendar-event-title">{{ event._calendarTitle || 'Untitled' }}</span>
              <span v-if="event._calendarDescription && !eventDescriptionStyle(event).hidden" class="calendar-event-description" :style="eventDescriptionStyle(event).style">{{ event._calendarDescription }}</span>
            </button>
            <button v-if="layout === 'month' && day.events.length > 3" type="button" class="calendar-more" @click="selectDay(day)">
              +{{ day.events.length - 3 }} more
            </button>
          </div>
        </template>
      </div>
      <div v-else-if="layout === 'day'" class="calendar-agenda" :data-layout="layout" role="group" :aria-label="periodLabel">
        <button
          v-for="event in dayEvents"
          :key="eventKey(event)"
          type="button"
          class="calendar-event"
          :style="eventTitleStyle(event).style"
          @click="openEvent(cursorDay, event)"
        >
          <span v-if="!eventTitleStyle(event).hidden" class="calendar-event-title">{{ event._calendarTitle || 'Untitled' }}</span>
          <span v-if="event._calendarDescription && !eventDescriptionStyle(event).hidden" class="calendar-event-description" :style="eventDescriptionStyle(event).style">{{ event._calendarDescription }}</span>
        </button>
        <div v-if="!dayEvents.length" class="calendar-note text-muted">Nothing on this day.</div>
      </div>
      <div v-else class="calendar-agenda" :data-layout="layout" role="group" :aria-label="periodLabel">
        <div v-for="day in listDays" :key="day.key" class="calendar-list-day">
          <button type="button" class="calendar-list-date" :data-day="day.key" :aria-current="day.key === selectedDay ? 'date' : undefined" @click="selectDay(day)">{{ day.label }}</button>
          <button
            v-for="event in day.events"
            :key="eventKey(event)"
            type="button"
            class="calendar-event"
            :style="eventTitleStyle(event).style"
            @click="openEvent(day, event)"
          >
            <span v-if="!eventTitleStyle(event).hidden" class="calendar-event-title">{{ event._calendarTitle || 'Untitled' }}</span>
            <span v-if="event._calendarDescription && !eventDescriptionStyle(event).hidden" class="calendar-event-description" :style="eventDescriptionStyle(event).style">{{ event._calendarDescription }}</span>
          </button>
        </div>
        <div v-if="!listDays.length" class="calendar-note text-muted">Nothing in this month.</div>
      </div>
      <div v-if="datedCount === 0" class="calendar-note text-muted">No records with a date to show.</div>
      <div v-else class="calendar-note text-muted">{{ datedCount }} dated record{{ datedCount === 1 ? '' : 's' }}</div>
      <div v-if="popupEvent" class="calendar-popup-backdrop" @click.self="closePopup">
        <div class="calendar-popup" role="dialog" aria-modal="true" :aria-label="popupEvent._calendarTitle || 'Event'" @keydown.esc="closePopup">
          <div class="calendar-detail-heading">{{ popupDayLabel }}</div>
          <h3 v-if="!eventTitleStyle(popupEvent).hidden" class="calendar-detail-title" :style="eventTitleStyle(popupEvent).style">{{ popupEvent._calendarTitle || 'Untitled' }}</h3>
          <p v-if="popupEvent._calendarDescription && !eventDescriptionStyle(popupEvent).hidden" class="calendar-detail-description" :style="eventDescriptionStyle(popupEvent).style">{{ popupEvent._calendarDescription }}</p>
          <div v-for="field in detailFields" :key="field.key" class="calendar-detail-field">
            <button
              v-if="field.linkMode === 'code'"
              type="button"
              :class="field.displayAsButton ? 'view-link-button' : 'calendar-nav'"
              @click="runLinkCode(popupEvent, field)"
            >{{ field.customText || field.label || 'Run' }}</button>
            <template v-else-if="field.type === 'link' && field.displayAsButton">
              <div v-if="field.label" class="calendar-detail-label">{{ field.label }}</div>
              <router-link
                v-if="fieldLinkHref(popupEvent, field)"
                :to="fieldLinkHref(popupEvent, field)"
                class="view-link-button"
              >{{ field.customText || popupEvent[field.key] || field.label || 'Open' }}</router-link>
              <span v-else class="view-link-button is-disabled">{{ field.customText || popupEvent[field.key] || field.label || 'Open' }}</span>
            </template>
            <template v-else>
              <div v-if="field.label" class="calendar-detail-label">{{ field.label }}</div>
              <div v-if="imageField(popupEvent, field)" v-html="fieldText(popupEvent, field)"></div>
              <div v-else-if="!fieldStyle(popupEvent, field).hidden" :class="{ 'multiselect-lines': listLines(field) }" :style="[fieldStyle(popupEvent, field).style, valueFormatStyle(field)]">{{ fieldText(popupEvent, field) }}</div>
            </template>
          </div>
          <div class="calendar-popup-actions">
            <button v-if="addFormUniqueTitle" type="button" class="calendar-nav" title="Add another record on this day" @click="addOnDay(popupEvent._calendarDate)">Add on this day</button>
            <button v-if="recurrenceColumn && popupRepeats" type="button" class="calendar-nav" @click="startOccurrenceEdit">Change this date</button>
            <button v-if="recurrenceColumn" type="button" class="calendar-nav" @click="startSeriesEdit">Edit the series</button>
            <button type="button" class="calendar-nav" @click="closePopup">Close</button>
          </div>
          <form v-if="editMode === 'occurrence'" class="calendar-edit" @submit.prevent="saveOccurrence">
            <label class="calendar-edit-choice"><input v-model="occurrenceAction" type="radio" value="override" /> Change this date</label>
            <label class="calendar-edit-choice"><input v-model="occurrenceAction" type="radio" value="skip" /> Skip this date</label>
            <label v-if="occurrenceAction === 'override'" class="calendar-edit-field">Date
              <input v-model="occurrenceDate" class="form-control form-control-sm" type="date" />
            </label>
            <label v-if="occurrenceAction === 'override'" class="calendar-edit-field">Title
              <input v-model="occurrenceTitle" class="form-control form-control-sm" type="text" />
            </label>
            <label v-if="occurrenceAction === 'override'" class="calendar-edit-field">Description
              <input v-model="occurrenceDescription" class="form-control form-control-sm" type="text" />
            </label>
            <button type="submit" class="calendar-nav" :disabled="recurrenceSaving">Save this date</button>
          </form>
          <form v-if="editMode === 'series'" class="calendar-edit" @submit.prevent="saveSeries">
            <label class="calendar-edit-field">Repeats
              <select v-model="seriesFrequency" class="form-control form-control-sm">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </label>
            <label class="calendar-edit-field">Every
              <input v-model.number="seriesInterval" class="form-control form-control-sm" type="number" min="1" max="99" />
            </label>
            <div v-if="seriesFrequency === 'weekly'" class="calendar-weekdays">
              <label v-for="(name, index) in weekdayNames" :key="name" class="calendar-edit-choice">
                <input type="checkbox" :checked="seriesWeekdays.indexOf(index) !== -1" @change="toggleWeekday(index)" />
                {{ name }}
              </label>
            </div>
            <label class="calendar-edit-field">Until
              <input v-model="seriesUntil" class="form-control form-control-sm" type="date" />
            </label>
            <button type="submit" class="calendar-nav" :disabled="recurrenceSaving">Save the series</button>
          </form>
          <p v-if="recurrenceError" class="calendar-edit-error">{{ recurrenceError }}</p>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import RuntimeFilterBuilder from './RuntimeFilterBuilder.vue'
import ViewTitle from './ViewTitle.vue'
import ViewSearch from './ViewSearch.vue'
import { displayListValue } from '../lib/multiselect-display'
import { formatFieldDisplay } from '../../../shared/value-format'
import { fieldDisplayValue, shownField } from '../../../shared/field-display'
import { realtimeSubscribeFields } from '../lib/view-visibility'
import { liveAppWebSocketUrl } from '../lib/live-app-socket'
import { eventRefreshesView, fieldIndexMap } from '../../../shared/view-refresh'
import { viewSearchParam } from '../../../shared/view-search'
import { filterQueryHref, readFilterQuery } from '../lib/filter-query'
import PresetFilterButtons from './PresetFilterButtons.vue'
import { presetFilterMixin } from '../lib/preset-filter-state'
import { CALENDAR_ROW_LIMIT, calendarDayKey, calendarFieldStyle, stampCalendarEvent } from '../../../shared/calendar-event'
import { browserTimeZone, calendarDayInTimeZone } from '../../../shared/app-timezone'
import {
  addDays,
  calendarFormDate,
  calendarRange,
  expandRecurrence,
  normalizeCalendarLayout,
  parseRecurrence,
  weekdayOf
} from '../../../shared/calendar-recurrence'
import { formatAppInstant } from '../lib/app-time-zone'
import { imageCellHtml, isViewImageMarkup } from '../../../shared/image-column'
import { runViewLinkCode } from '../lib/run-custom-code'
import { viewLinkHref, viewRecordLink } from '../../../shared/view-fields'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const LAYOUTS = [
  { id: 'month', label: 'Month' },
  { id: 'week', label: 'Week' },
  { id: 'day', label: 'Day' },
  { id: 'list', label: 'List' }
]

export default {
  mixins: [presetFilterMixin],
  components: {
    RuntimeFilterBuilder,
    ViewTitle,
    ViewSearch,
    PresetFilterButtons
  },
  data () {
    const today = new Date()
    return {
      filterFieldGroups: [],
      filteredItems: null,
      urlFilters: null,
      urlMatchMode: null,
      activeFilterEvent: null,
      searchText: '',
      ws: null,
      icalDownloading: false,
      weekdayNames: WEEKDAYS,
      layoutChoices: LAYOUTS,
      layout: 'month',
      cursor: new Date(today.getFullYear(), today.getMonth(), 1),
      monthChosen: false,
      selectedDay: '',
      selectedEventId: null,
      popupEvent: null,
      editMode: '',
      occurrenceAction: 'override',
      occurrenceDate: '',
      occurrenceTitle: '',
      occurrenceDescription: '',
      seriesFrequency: 'weekly',
      seriesInterval: 1,
      seriesWeekdays: [],
      seriesUntil: '',
      recurrenceSaving: false,
      recurrenceError: '',
      timeZone: browserTimeZone()
    }
  },
  props: {
    title: { type: String, default: '' },
    uniqueTitle: { type: String, default: '' },
    userFiltering: { type: Boolean },
    realtime: { type: Boolean, default: false },
    spreadSheetUniqueTitle: { type: String, default: '' },
    fields: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    pageUniqueTitle: { type: String, default: '' },
    componentUniqueTitle: { type: String, default: '' },
    fieldCounterMap: { type: Object, default: null },
    defaultUserFilters: { type: Array, default: () => [] },
    defaultUserFilterMatchMode: { type: String, default: 'all' },
    restrictUserFiltering: { type: Boolean, default: false },
    searchEnabled: { type: Boolean, default: false },
    dateColumn: { type: String, default: '' },
    titleColumn: { type: String, default: '' },
    descriptionColumn: { type: String, default: '' },
    recurrenceColumn: { type: String, default: '' },
    calendarLayout: { type: String, default: 'month' },
    addFormUniqueTitle: { type: String, default: '' }
  },
  computed: {
    displayItems () {
      return this.filteredItems !== null ? this.filteredItems : this.items
    },
    calendarView () {
      return {
        type: 'calendar',
        dateColumn: this.dateColumn,
        titleColumn: this.titleColumn,
        descriptionColumn: this.descriptionColumn,
        recurrenceColumn: this.recurrenceColumn
      }
    },
    datedItems () {
      return (this.displayItems || []).filter(item => this.dayOf(item))
    },
    datedCount () {
      return this.datedItems.length
    },
    todayKey () {
      return calendarDayInTimeZone(new Date(), this.timeZone) || calendarDayKey(new Date())
    },
    cursorKey () {
      return calendarDayKey(this.cursor)
    },
    visibleRange () {
      return calendarRange(this.layout, this.cursorKey)
    },
    occurrenceItems () {
      const range = this.visibleRange
      if (!range) return []
      const items = []
      for (const item of this.displayItems || []) {
        const anchor = this.dayOf(item)
        if (!anchor) continue
        const occurrences = expandRecurrence({
          anchor,
          rule: item._calendarRule,
          rangeStart: range.start,
          rangeEnd: range.end,
          timeZone: this.timeZone
        })
        for (const occurrence of occurrences) {
          items.push({
            ...item,
            _calendarDate: occurrence.date,
            _seriesDate: occurrence.seriesDate,
            _calendarException: occurrence.exception,
            _calendarTitle: occurrence.title || item._calendarTitle,
            _calendarDescription: occurrence.description || item._calendarDescription
          })
        }
      }
      return items
    },
    eventsByDay () {
      const grouped = {}
      for (const item of this.occurrenceItems) {
        const day = item._calendarDate
        if (!day) continue
        if (!grouped[day]) grouped[day] = []
        grouped[day].push(item)
      }
      return grouped
    },
    rangeDays () {
      const range = this.visibleRange
      if (!range) return []
      const month = this.cursor.getMonth()
      const days = []
      let key = range.start
      while (key && key <= range.end) {
        const parts = key.split('-').map(Number)
        const date = new Date(parts[0], parts[1] - 1, parts[2])
        days.push({
          key,
          date,
          inMonth: this.layout === 'week' || date.getMonth() === month,
          isToday: key === this.todayKey,
          events: this.eventsByDay[key] || [],
          label: date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
        })
        key = addDays(key, 1)
      }
      return days
    },
    weeks () {
      if (this.layout !== 'month' && this.layout !== 'week') return []
      const weeks = []
      const days = this.rangeDays
      for (let index = 0; index < days.length; index += 7) weeks.push(days.slice(index, index + 7))
      return weeks
    },
    listDays () {
      return this.layout === 'list' ? this.rangeDays : []
    },
    dayEvents () {
      return this.eventsByDay[this.cursorKey] || []
    },
    cursorDay () {
      const parts = String(this.cursorKey || '').split('-').map(Number)
      return {
        key: this.cursorKey,
        date: new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1),
        events: this.dayEvents
      }
    },
    periodLabel () {
      if (this.layout === 'day') {
        return this.cursor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
      }
      if (this.layout === 'week' && this.rangeDays.length) {
        const start = this.rangeDays[0].date
        const end = this.rangeDays[this.rangeDays.length - 1].date
        const sameYear = start.getFullYear() === end.getFullYear()
        const startText = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' })
        const endText = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        return `${startText} – ${endText}`
      }
      return this.cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    },
    popupRepeats () {
      return !!parseRecurrence(this.popupEvent && this.popupEvent._calendarRule)
    },
    popupDayLabel () {
      const day = this.popupEvent && this.popupEvent._calendarDate
      if (!day) return ''
      const parts = day.split('-').map(Number)
      return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    },
    selectedEvents () {
      if (!this.selectedDay) return []
      const events = this.eventsByDay[this.selectedDay] || []
      if (this.selectedEventId == null) return events
      const chosen = events.filter(event => this.eventKey(event) === this.selectedEventId)
      return chosen.length ? chosen : events
    },
    selectedDayLabel () {
      if (!this.selectedDay) return ''
      const parts = this.selectedDay.split('-').map(Number)
      const date = new Date(parts[0], parts[1] - 1, parts[2])
      return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    },
    detailFields () {
      return (this.fields || []).filter(field => {
        const id = field && field.columnUniqueTitle
        return id && id !== this.dateColumn && id !== this.titleColumn && id !== this.descriptionColumn
      })
    },
    filterQueryKey () {
      return `f_${this.componentUniqueTitle || this.uniqueTitle}`
    },
    searchQueryKey () {
      return viewSearchParam(this.componentUniqueTitle || this.uniqueTitle)
    },
    searchTerm () {
      return String(this.searchText || '').trim()
    },
    resolvedInitialFilters () {
      if (this.urlFilters) return this.urlFilters
      return this.defaultUserFilters
    },
    resolvedInitialMatchMode () {
      if (this.urlMatchMode) return this.urlMatchMode
      return this.defaultUserFilterMatchMode
    }
  },
  mounted () {
    this.layout = normalizeCalendarLayout(this.calendarLayout)
    this.alignMonthToEvents()
    if (this.userFiltering) {
      this.readFiltersFromUrl()
      this.loadFilterFields()
    }
    if (this.searchEnabled) {
      this.readSearchFromUrl()
      if (this.searchTerm) this.fetchCalendarData(this.activeFilterEvent, { alwaysApply: true })
    }
    if (this.realtime) this.connectRealtimeWebSocket()
    this._onRefreshViews = (event) => {
      if (!eventRefreshesView(event, this.componentUniqueTitle || this.uniqueTitle)) return
      this.refreshData()
    }
    window.addEventListener('superturbo-refresh-views', this._onRefreshViews)
  },
  beforeUnmount () {
    clearTimeout(this._searchTimer)
    this.disconnectRealtimeWebSocket()
    if (this._onRefreshViews) window.removeEventListener('superturbo-refresh-views', this._onRefreshViews)
  },
  watch: {
    displayItems () {
      if (!this.monthChosen) this.alignMonthToEvents()
    },
    userFiltering (val) {
      if (val && !this.filterFieldGroups.length) this.loadFilterFields()
    }
  },
  methods: {
    alignMonthToEvents () {
      const first = this.datedItems[0]
      const day = first && this.dayOf(first)
      if (!day) return
      const parts = day.split('-').map(Number)
      if (this.layout === 'week' || this.layout === 'day') {
        this.cursor = new Date(parts[0], parts[1] - 1, parts[2])
        this.selectedDay = day
        return
      }
      this.cursor = new Date(parts[0], parts[1] - 1, 1)
    },
    dayOf (item) {
      if (!item) return null
      if (item._calendarInstant != null && item._calendarInstant !== '') {
        return calendarDayInTimeZone(item._calendarInstant, this.timeZone) || item._calendarDate || null
      }
      return item._calendarDate || null
    },
    goToday () {
      this.monthChosen = true
      const todayKey = this.todayKey
      const parts = todayKey.split('-').map(Number)
      if (this.layout === 'month' || this.layout === 'list') this.cursor = new Date(parts[0], parts[1] - 1, 1)
      else this.cursor = new Date(parts[0], parts[1] - 1, parts[2])
      this.selectDay({ key: todayKey, events: this.eventsByDay[todayKey] || [] })
    },
    visibleEvents (day) {
      if (this.layout === 'week') return day.events
      return day.events.slice(0, 3)
    },
    eventKey (event) {
      if (!event) return ''
      const day = event._seriesDate || event._calendarDate || ''
      if (event._rowId != null) return `${event._rowId}:${day}`
      return `${day}:${event._calendarTitle || ''}`
    },
    setLayout (layout) {
      const next = normalizeCalendarLayout(layout)
      const anchor = this.selectedDay || this.cursorKey
      const parts = String(anchor || '').split('-').map(Number)
      this.monthChosen = true
      this.layout = next
      if (next === 'month' || next === 'list') this.cursor = new Date(parts[0], parts[1] - 1, 1)
      else this.cursor = new Date(parts[0], parts[1] - 1, parts[2] || 1)
      if (!this.selectedDay) this.selectedDay = anchor
    },
    shiftPeriod (amount) {
      this.monthChosen = true
      if (this.layout === 'week') {
        this.cursor = new Date(this.cursor.getFullYear(), this.cursor.getMonth(), this.cursor.getDate() + (amount * 7))
        return
      }
      if (this.layout === 'day') {
        this.cursor = new Date(this.cursor.getFullYear(), this.cursor.getMonth(), this.cursor.getDate() + amount)
        this.selectedDay = calendarDayKey(this.cursor)
        return
      }
      this.cursor = new Date(this.cursor.getFullYear(), this.cursor.getMonth() + amount, 1)
    },
    addOnSelectedDay () {
      this.addOnDay(this.selectedDay)
    },
    addOnDay (day) {
      const formUniqueTitle = this.addFormUniqueTitle
      const date = calendarFormDate(day)
      if (!formUniqueTitle || !date || !this.dateColumn) return
      window.dispatchEvent(new CustomEvent('superturbo-calendar-prefill', {
        detail: {
          formUniqueTitle,
          values: { [this.dateColumn]: date }
        }
      }))
    },
    openEvent (day, event) {
      this.selectEvent(day, event)
      this.popupEvent = event
      this.editMode = ''
      this.recurrenceError = ''
    },
    runLinkCode (event, field) {

      return runViewLinkCode(field && field.code, event)
    },
    fieldLinkHref (item, field) {

      return viewLinkHref(viewRecordLink(item, field), {
        routePath: this.routePath || (this.$route && this.$route.path) || '',
        application: this.$route && this.$route.params ? this.$route.params.application : ''
      })
    },
    closePopup () {
      this.popupEvent = null
      this.editMode = ''
      this.recurrenceError = ''
    },
    startOccurrenceEdit () {
      const event = this.popupEvent
      if (!event) return
      this.editMode = 'occurrence'
      this.occurrenceAction = 'override'
      this.occurrenceDate = event._calendarDate || event._seriesDate || ''
      this.occurrenceTitle = ''
      this.occurrenceDescription = ''
      this.recurrenceError = ''
    },
    startSeriesEdit () {
      const rule = parseRecurrence(this.popupEvent && this.popupEvent._calendarRule) || {}
      const anchor = (this.popupEvent && this.popupEvent._seriesDate) || this.dayOf(this.popupEvent)
      this.editMode = 'series'
      this.seriesFrequency = rule.frequency || 'weekly'
      this.seriesInterval = rule.interval || 1
      this.seriesWeekdays = (rule.weekdays && rule.weekdays.length) ? rule.weekdays.slice() : (anchor && weekdayOf(anchor) != null ? [weekdayOf(anchor)] : [])
      this.seriesUntil = rule.until || ''
      this.recurrenceError = ''
    },
    toggleWeekday (index) {
      const days = this.seriesWeekdays.slice()
      const at = days.indexOf(index)
      if (at === -1) days.push(index)
      else days.splice(at, 1)
      this.seriesWeekdays = days.sort((left, right) => left - right)
    },
    async saveOccurrence () {
      const event = this.popupEvent
      if (!event || event._rowId == null) return
      await this.saveRecurrence({
        recordId: event._rowId,
        scope: 'occurrence',
        seriesDate: event._seriesDate || event._calendarDate,
        exception: {
          action: this.occurrenceAction,
          occurrenceDate: this.occurrenceAction === 'override' ? this.occurrenceDate : '',
          title: this.occurrenceTitle,
          description: this.occurrenceDescription
        }
      })
    },
    async saveSeries () {
      const event = this.popupEvent
      if (!event || event._rowId == null) return
      await this.saveRecurrence({
        recordId: event._rowId,
        scope: 'series',
        rule: {
          frequency: this.seriesFrequency,
          interval: this.seriesInterval,
          weekdays: this.seriesFrequency === 'weekly' ? this.seriesWeekdays : [],
          until: this.seriesUntil
        }
      })
    },
    async saveRecurrence (body) {
      if (this.recurrenceSaving) return
      this.recurrenceSaving = true
      this.recurrenceError = ''
      try {
        await axios.post(this.calendarUrl('/recurrence'), body)
        await this.refreshData()
        this.closePopup()
      } catch (err) {
        const data = err && err.response && err.response.data
        this.recurrenceError = typeof data === 'string' && data.trim() ? data : 'Could not save the repeat rule.'
      } finally {
        this.recurrenceSaving = false
      }
    },
    selectDay (day) {
      this.selectedDay = day.key
      this.selectedEventId = null
    },
    onDayKey (day, event) {
      const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key]
      if (!step) return
      event.preventDefault()
      const next = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate() + step)
      const key = calendarDayKey(next)
      const range = this.visibleRange
      if (!range || key < range.start || key > range.end) {
        this.monthChosen = true
        if (this.layout === 'month' || this.layout === 'list') this.cursor = new Date(next.getFullYear(), next.getMonth(), 1)
        else this.cursor = next
      }
      this.selectDay({ key, events: this.eventsByDay[key] || [] })
      this.$nextTick(() => {
        const button = this.$el.querySelector(`[data-day="${key}"]`)
        if (button) button.focus()
      })
    },
    selectEvent (day, event) {
      this.selectedDay = day.key
      this.selectedEventId = this.eventKey(event)
    },
    eventTitleStyle (event) {
      return calendarFieldStyle(event && event._formatting, this.titleColumn)
    },
    eventDescriptionStyle (event) {
      return calendarFieldStyle(event && event._formatting, this.descriptionColumn)
    },
    fieldStyle (event, field) {
      return calendarFieldStyle(event && event._formatting, field && field.columnUniqueTitle)
    },
    valueFormatStyle (field) {
      const display = formatFieldDisplay(' ', field && field.valueFormatting)
      return (display && display.style) || {}
    },
    listLines (field) {
      const mode = field && field.multiselectDisplay
      return mode === 'newline' || mode === 'bullets'
    },
    fieldText (event, field) {
      const raw = event && event._raw && event._raw[field.key] != null ? event._raw[field.key] : (event ? event[field.key] : '')
      const shown = shownField(fieldDisplayValue(raw, event ? event[field.key] : ''), {
        multiselectDisplay: field && field.multiselectDisplay,
        valueFormatting: field && field.valueFormatting
      })
      if (shown) return shown.text
      const value = event ? event[field.key] : ''
      if (value == null) return ''
      return typeof value === 'string' ? value : String(value)
    },
    imageField (event, field) {
      return isViewImageMarkup(this.fieldText(event, field))
    },
    async loadFilterFields () {
      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      if (!pageUT || !compUT) return
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''
      try {
        let url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/filterfields`
        if (this.restrictUserFiltering) url += '?restrict=true'
        const response = await axios.get(url)
        this.filterFieldGroups = response.data
      } catch (err) {
        console.warn('Failed to load filter fields:', err)
      }
    },
    compactFilter (filter) {
      const compact = {}
      for (const [key, value] of Object.entries(filter)) {
        if (value === null || value === undefined) continue
        compact[key] = key === 'valueFieldRef' && value !== null ? this.compactFilter(value) : value
      }
      return compact
    },
    expandFilter (compact) {
      const full = {
        relationshipColumn: null,
        relatedSpreadsheet: null,
        sourceSpreadsheet: null,
        sourceRelationshipColumn: null,
        relQuantifier: null,
        valueType: 'literal',
        value: '',
        valueFieldRef: null,
        ...compact
      }
      if (full.valueFieldRef && typeof full.valueFieldRef === 'object') {
        full.valueFieldRef = this.expandFilter(full.valueFieldRef)
      }
      return full
    },
    filtersMatchDefaults (filterEvent) {
      const defaults = this.defaultUserFilters || []
      const current = (filterEvent && filterEvent.filters) || []
      const currentMode = (filterEvent && filterEvent.matchMode) || 'all'
      const defaultMode = this.defaultUserFilterMatchMode || 'all'
      if (currentMode !== defaultMode) return false
      if (current.length !== defaults.length) return false
      return JSON.stringify(current) === JSON.stringify(defaults)
    },
    readFiltersFromUrl () {
      const parsed = readFilterQuery(window.location.search, this.filterQueryKey)
      if (!parsed) return
      this.urlFilters = parsed.filters.map(filter => this.expandFilter(filter))
      this.urlMatchMode = parsed.matchMode
    },
    writeFiltersToUrl (filterEvent) {
      try {
        const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
        let payload = null
        if (hasFilters && !this.filtersMatchDefaults(filterEvent)) {
          payload = { filters: filterEvent.filters.map(filter => this.compactFilter(filter)) }
          if ((filterEvent.matchMode || 'all') !== 'all') payload.matchMode = filterEvent.matchMode
        }
        window.history.replaceState(null, '', filterQueryHref(window.location.href, this.filterQueryKey, payload))
      } catch (err) {
        console.warn('Failed to write filters to URL:', err)
      }
    },
    readSearchFromUrl () {
      try {
        const params = new URLSearchParams(window.location.search)
        this.searchText = params.get(this.searchQueryKey) || ''
      } catch (err) {
        this.searchText = ''
      }
    },
    writeSearchToUrl () {
      try {
        const url = new URL(window.location.href)
        if (this.searchTerm) url.searchParams.set(this.searchQueryKey, this.searchTerm)
        else url.searchParams.delete(this.searchQueryKey)
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
      } catch (err) {
        console.warn('Failed to write search to URL:', err)
      }
    },
    onSearchInput (value) {
      this.searchText = value
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.writeSearchToUrl()
        this.fetchCalendarData(this.activeFilterEvent)
      }, 300)
    },
    async onFilterChanged (filterEvent) {
      this.writeFiltersToUrl(filterEvent)
      this.activeFilterEvent = filterEvent
      await this.fetchCalendarData(filterEvent)
    },
    reloadForPresetFilters () {
      return this.fetchCalendarData(this.activeFilterEvent, { alwaysApply: true })
    },
    async refreshData () {
      if (window.__superturbo_preview_mode) return
      await this.fetchCalendarData(this.activeFilterEvent, { alwaysApply: true })
    },
    calendarQuery (extra) {
      const params = new URLSearchParams()
      const filterEvent = this.activeFilterEvent
      const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
      if (hasFilters) {
        params.set('userFilters', JSON.stringify(filterEvent.filters))
        params.set('userFilterMatchMode', filterEvent.matchMode || 'all')
      }
      if (this.searchEnabled && this.searchTerm) params.set('search', this.searchTerm)
      if (this.presetFilterQuery()) params.set('presetFilters', this.presetFilterQuery())
      const accessKey = this.routeAccessKey
      if (accessKey) params.set('accessKey', accessKey)
      if (extra) {
        for (const [key, value] of Object.entries(extra)) {
          if (value != null && value !== '') params.set(key, String(value))
        }
      }
      return params
    },
    calendarUrl (suffix, extra) {
      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''
      const qs = this.calendarQuery(extra).toString()
      const path = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}${suffix || ''}`
      return qs ? `${path}?${qs}` : path
    },
    icalDownloadName () {
      const base = String(this.title || this.uniqueTitle || 'calendar').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'calendar'
      return base.toLowerCase().endsWith('.ics') ? base : `${base}.ics`
    },
    async downloadIcal () {
      if (window.__superturbo_preview_mode || this.icalDownloading) return
      this.icalDownloading = true
      try {
        const response = await axios.get(this.calendarUrl('/ics'), {
          responseType: 'blob',
          headers: { Accept: 'text/calendar' }
        })
        const blob = response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: 'text/calendar;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = this.icalDownloadName()
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      } catch (err) {
        console.warn('Failed to download iCal:', err)
      } finally {
        this.icalDownloading = false
      }
    },
    async fetchCalendarData (filterEvent, { alwaysApply = false } = {}) {
      const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
      const hasSearch = this.searchEnabled && !!this.searchTerm
      const hasPreset = this.activePresetFilterIds.length > 0
      if (!alwaysApply && !hasFilters && !hasSearch && !hasPreset) {
        this.filteredItems = null
        return
      }
      const url = this.calendarUrl('', { limit: CALENDAR_ROW_LIMIT })
      const requestId = (this._calendarRequestId = (this._calendarRequestId || 0) + 1)
      try {
        const response = await axios.get(url)
        if (requestId !== this._calendarRequestId) return
        this.filteredItems = this.transformResponseData(response.data)
      } catch (err) {
        if (requestId !== this._calendarRequestId) return
        console.warn('Failed to fetch calendar data:', err)
      }
    },
    transformResponseData (data) {
      const index = fieldIndexMap(this.fieldCounterMap, this.fields)
      return _.map(data || [], (row) => {
        const newRow = { _values: row }
        if (row._links) newRow._links = row._links
        if (row.links) newRow.links = row.links
        if (row.id !== undefined) newRow._rowId = row.id
        if (index) {
          for (const key of Object.keys(row)) {
            if (key === '_formatting' || key === '_links' || key === 'links') continue
            if (key.endsWith('_filename') || key.endsWith('_type')) continue
            if (typeof index[key] !== 'number') continue
            const column = row[key]
            if (!newRow._raw) newRow._raw = {}
            newRow._raw[index[key]] = column
            const imageHtml = imageCellHtml(row, key)
            const field = (this.fields || []).find((item) => item.columnUniqueTitle === key)
            const listed = displayListValue(column, field && field.multiselectDisplay)
            if (imageHtml != null) newRow[index[key]] = imageHtml
            else if (listed != null) newRow[index[key]] = listed
            else newRow[index[key]] = typeof column === 'string' ? formatAppInstant(column) : column
          }
        }
        stampCalendarEvent(newRow, row, this.calendarView)
        return newRow
      })
    },
    connectRealtimeWebSocket () {
      try {
        this.ws = new WebSocket(liveAppWebSocketUrl(VUE_APP_APP_SERVER_WS_URL, this.routeAccount, this.$route.params.application))
      } catch (err) {
        console.warn('Realtime WebSocket connection failed:', err)
        return
      }
      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({
          type: 'realtime_subscribe',
          pageUniqueTitle: this.pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          ...realtimeSubscribeFields({
            ...(this.$route && this.$route.params),
            account: this.routeAccount
          })
        }))
      }
      this.ws.onmessage = (event) => {
        let msg
        try { msg = JSON.parse(event.data) } catch { return }
        if (msg.type === 'realtime_update' &&
            msg.pageUniqueTitle === this.pageUniqueTitle &&
            msg.componentUniqueTitle === this.componentUniqueTitle) {
          if ((this.searchEnabled && this.searchTerm) || this.activePresetFilterIds.length) {
            this.fetchCalendarData(this.activeFilterEvent, { alwaysApply: true })
          } else {
            this.filteredItems = this.transformResponseData(msg.data)
          }
        }
      }
      this.ws.onclose = () => {
        setTimeout(() => {
          if (this.realtime && !this._isBeingDestroyed) this.connectRealtimeWebSocket()
        }, 3000)
      }
      this.ws.onerror = () => {}
    },
    disconnectRealtimeWebSocket () {
      if (!this.ws) return
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: 'realtime_unsubscribe',
          pageUniqueTitle: this.pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle
        }))
      }
      this.ws.onclose = null
      this.ws.close()
      this.ws = null
    }
  }
}
</script>

<style scoped>
.calendar-container {
  padding: 1rem 0;
}

.calendar-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  margin-bottom: 0.75rem;
}

.calendar-nav-group,
.calendar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.calendar-actions {
  margin-left: auto;
}

.calendar-layouts {
  display: flex;
  gap: 0.25rem;
}

.calendar-month {
  min-width: 8rem;
  text-align: center;
  font-weight: 600;
}

.calendar-nav,
.calendar-day-number,
.calendar-event,
.calendar-more {
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  color: inherit;
}

.calendar-nav {
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  padding: 0.25rem 0.6rem;
  background: #fff;
}

.calendar-nav.is-active {
  background: var(--bs-primary, #0d6efd);
  border-color: var(--bs-primary, #0d6efd);
  color: #fff;
}

.calendar-nav:disabled {
  opacity: 0.45;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1px;
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}

.calendar-grid.is-week .calendar-day {
  min-height: 12rem;
}

.calendar-dow,
.calendar-day {
  background: #fff;
  min-width: 0;
}

.calendar-dow {
  padding: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  color: #6b7280;
}

.calendar-day {
  min-height: 6.5rem;
  padding: 0.25rem;
}

.calendar-day.is-outside {
  background: #f8fafc;
  color: #94a3b8;
}

.calendar-day.is-today .calendar-day-number {
  background: #1d4ed8;
  color: #fff;
}

.calendar-day.is-selected {
  outline: 2px solid #93c5fd;
  outline-offset: -2px;
}

.calendar-day-number {
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.calendar-event {
  display: block;
  width: 100%;
  margin-top: 0.2rem;
  padding: 0.15rem 0.3rem;
  border-radius: 4px;
  background: #dbeafe;
  cursor: pointer;
}

.calendar-event-title,
.calendar-event-description {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calendar-event-title {
  font-size: 0.75rem;
  font-weight: 600;
}

.calendar-event-description {
  font-size: 0.7rem;
  color: #334155;
}

.calendar-more {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.7rem;
  color: #1d4ed8;
}

.calendar-note {
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.calendar-detail {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
}

.calendar-detail-heading {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.calendar-detail-event + .calendar-detail-event {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.calendar-detail-title {
  font-size: 1rem;
  margin: 0 0 0.25rem;
}

.calendar-detail-description {
  margin: 0 0 0.5rem;
}

.calendar-detail-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
}

.calendar-agenda {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 0.5rem;
}

.calendar-list-day {
  display: grid;
  grid-template-columns: 8.5rem minmax(0, 1fr);
  gap: 0.35rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.calendar-list-date {
  border: 0;
  background: transparent;
  text-align: left;
  font-weight: 600;
  color: inherit;
}

.calendar-popup-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.45);
}

.calendar-popup {
  width: min(32rem, 100%);
  max-height: min(40rem, 100%);
  overflow: auto;
  padding: 1rem 1.1rem 1.2rem;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.2);
}

.calendar-popup-actions,
.calendar-edit,
.calendar-weekdays {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.75rem;
}

.calendar-edit {
  flex-direction: column;
  align-items: stretch;
}

.calendar-edit-field,
.calendar-edit-choice {
  display: flex;
  gap: 0.4rem;
  align-items: center;
  font-size: 0.875rem;
}

.calendar-edit-field input,
.calendar-edit-field select {
  flex: 1;
  min-width: 0;
}

.calendar-edit-error {
  margin: 0.6rem 0 0;
  color: #b91c1c;
  font-size: 0.875rem;
}

@media (max-width: 720px) {
  .calendar-grid {
    font-size: 0.75rem;
  }

  .calendar-day {
    min-height: 4.5rem;
  }

  .calendar-event-description {
    display: none;
  }
}
</style>
